<template>
<section class="charts-wrapper">
  <section class="chart-item" style="display:grid;grid-template-rows:max-content auto;grid-row-gap:12px;height: 100%;min-width: 1480px;">
    <div style="display:flex;justify-content:center;">
      <select-ctrl-group :ids="selectedNames" :options="filterFactors" id-prop="name" />
    </div>
    <!--  style="min-height:0;" -->
    <div>
      <e-chart ref="ion-ocec-chart" @initialized="onInitialized" @destroy="onDestroy" />
    </div>
  </section>
  <!-- <section class="chart-item">
  </section> -->
</section>
</template>

<script>
import BaseStore from '../../../../components/BaseStore'
import EChart from '../../../../components/Charts/ECharts'
import {FACTOR_OPTS} from './constants'
import SelectCtrlGroup from '../../../../components/SelectCtrlGroup/index'

export default {
  name: 'DailyAnalysis',
  components: {SelectCtrlGroup, EChart},
  extends: BaseStore,
  props: {
    query: {
      required: true,
      type: Object
    },
    compareQuery: {
      type: Object
    }
  },
  data: () => ({
    selectedNames: []
  }),
  computed: {
    tsStep: () => 86400000,
    beginTS () {
      return new Date(this.query.beginTime).getTime()
    },
    siteMap () {
      return this.profileSites.reduce((map, site) => {
        map[site.id] = site
        return map
      }, {})
    },
    filterFactors: () => FACTOR_OPTS.filter(factorOpt => factorOpt.name)
  },
  methods: {
    getData () {
      let {query} = this
      this.changeLoadStatus(true)
      this.$api.Data.getHistoryData({
        beginTime: query.beginTime,
        endTime: query.endTime,
        dataType: query.dataType,
        isAuditCompleted: query.isAuditCompleted,
        isGetInvalidData: query.isGetInvalidData,
        isMultipleIntegral: query.isMultipleIntegral,
        isFuse: query.isFuse,
        siteIds: query.siteIds,
        factorIds: query.factorIds,
        granularity: 'day'
      }).then(dataList => {
        let dataTimes = this.buildDataTimes(dataList)
        this.onDataReceived(dataTimes, dataList)
      }).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    },
    onDataReceived (dataTimes, dataList) {
      this.renderData(dataTimes, this.formatDataList(dataTimes, dataList))
    },
    buildDataTimes (dataList) {
      if (dataList.length === 0) {
        return []
      }

      // let {endTime} = this.query
      let {beginTS, tsStep} = this
      // let endTS = new Date(endTime).getTime()
      let endTS = Math.max(...dataList.map(data => new Date(data.dataTimeStr).getTime()))
      let dataTimes = []

      for (let ts = beginTS; ts <= endTS; ts += tsStep) {
        let date = new Date(ts)
        dataTimes.push({
          ts,
          dt: date.format('MM-DD')
        })
      }

      return dataTimes
    },
    formatDataList (dataTimes, dataList) {
      let {siteIds} = this.query
      let {siteMap, beginTS, tsStep, filterFactors, factorMap} = this
      let dataMap = siteIds.reduce((map, siteId) => {
        map[siteId] = {
          id: siteId,
          name: siteMap[siteId].name,
          data: dataTimes.map(dataTime => ({
            ts: dataTime.ts,
            dataTime: dataTime.dt
          }))
        }

        return map
      }, {})

      dataList.forEach(data => {
        let ts = new Date(data.dataTimeStr).getTime()
        let index = (ts - beginTS) / tsStep
        let siteData = dataMap[data.siteId].data
        siteData[index][data.factorId] = data.value
      })

      dataList = Object.values(dataMap)

      dataList.forEach(siteData => {
        siteData.data = siteData.data.map(data => filterFactors.reduce((map, factor) => {
          map[factor.id] = factor.getVal(data, factorMap)
          return map
        }, {
          dataTime: data.dataTime,
          ts: data.ts
        }))
      })

      return dataList
    },
    renderData (dataTimes, dataList) {
      const AXIS_OFFSET = 50
      let {filterFactors} = this
      let units = [...new Set(filterFactors.map(factor => factor.unit))]
      let yAxis = units.map((unit, index) => ({
        type: 'value',
        name: unit,
        offset: Math.max(index - 1, 0) * AXIS_OFFSET,
        axisLine: {
          show: true
        },
        splitLine: {
          show: index === 0
        }
      }))
      let unitMap = units.reduce((map, unit, index) => {
        map[unit] = index
        return map
      }, {})

      let chartOpt = {
        title: {
          text: '离子和OCEC 污染特征分析',
          left: 'center'
        },
        toolbox: {
          feature: {
            saveAsImage: {}
          },
          top: -6,
          right: 23
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'none'
          },
          appendToBody: true,
          formatter: params => params[0].axisValue ? params.reduce((list, {marker, seriesName, data: {value, unit}}) => {
            list.push(`<div style="font-size:12px;">${marker}<span>${seriesName}</span><span style="padding-left:20px;float:right;">${Number.isFinite(value) ? value : '--'} ${unit || ''}</span></div>`)
            return list
          }, [`<div style="font-size:14px;color:#666;font-weight:400;line-height:1;margin-bottom:5px;">${params[0].axisValue}</div>`]).join('') : ''
        },
        legend: {
          top: 30,
          selected: this.getSelected()
        },
        grid: {
          top: 54,
          right: (yAxis.length - 1) * AXIS_OFFSET,
          bottom: 65,
          left: AXIS_OFFSET
        },
        xAxis: [{
          type: 'category',
          data: dataList.reduce(list => {
            list.push('')
            dataTimes.forEach(dataTime => {
              list.push(dataTime.dt)
            })
            list.push('')
            return list
          }, []),
          axisLabel: {
            rotate: 45,
            interval: 0
          },
          axisTick: {
            show: false
          },
          interval: 0
        }, {
          type: 'category',
          data: dataList.map(data => data.name),
          position: 'bottom',
          offset: 40
        }],
        yAxis,
        series: filterFactors.map(({id, name, unit, chart}) => ({
          name,
          type: chart,
          yAxisIndex: unitMap[unit],
          stack: chart === 'bar' ? 'ion-ocec' : null,
          barMaxWidth: 30,
          data: dataList.reduce((list, data) => {
            list.push(null)
            data.data.forEach(values => {
              list.push({
                value: values[id],
                unit
              })
            })
            list.push(null)
            return list
          }, [])
        }))
      }

      this.$refs['ion-ocec-chart'].setOpt(chartOpt)
    },
    getSelected () {
      let factorIdMap = this.selectedNames.reduce((map, name) => {
        map[name] = true
        return map
      }, {})

      this.filterFactors.reduce((map, {name}) => {
        if (!map[name]) {
          map[name] = false
        }

        return map
      }, factorIdMap)

      return factorIdMap
    },
    onLegendSelectChanged (param) {
      this.selected = Object.freeze(JSON.parse(JSON.stringify(param.selected)))
      this.selectedNames = Object.entries(this.selected).filter(selected => selected[1]).map(selected => selected[0])
    },
    onInitialized (chartObj) {
      this.initialized = true
      chartObj.on('legendselectchanged', this.onLegendSelectChanged)
    },
    onDestroy (chartObj) {
      chartObj.off('legendselectchanged', this.onLegendSelectChanged)
      this.initialized = false
    }
  },
  watch: {
    filterFactors: {
      immediate: true,
      handler (filterFactors) {
        if (filterFactors.length === 0) {
          return
        }

        this.selectedNames = filterFactors.map(factor => factor.name)
      }
    },
    selectedNames (selectedNames, old) {
      if (selectedNames !== old) {
        return
      }

      let chartObj = this.$refs['ion-ocec-chart']
      let chartOpt = chartObj.getOpt()

      if (!chartOpt) {
        return
      }

      chartObj.setOpt({
        legend: {
          selected: this.getSelected()
        }
      }, true)
    }
  }
}
</script>

<style lang="scss" scoped>
.charts-wrapper {
  width: 100%;
  height: 100%;
  overflow: auto;

  // > .chart-item {
  //   width: 100%;
  //   height: 50%;
  //   min-height: 300px;
  // }
}
</style>



// WEBPACK FOOTER //
// src/views/04Statistical-Analysis/03Pollution/02Pollutant/DailyAnalysis.vue